<!-- Hero Section -->
<section class="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-900">
  <!-- Animated Background Elements -->
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900/20"></div>
    <div class="absolute top-[-10%] right-[-5%] w-96 h-96 bg-blue-600/20 rounded-full blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <!-- Grid Pattern Overlay -->
  <div class="absolute inset-0 z-0 opacity-[0.03]">
    <div class="absolute inset-0" style="background-image: radial-gradient(circle at 10% 20%, rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 30px 30px;"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 mb-6 backdrop-blur-sm">
          <i class="bx bx-shield-quarter text-lg"></i>
          <span class="text-sm font-semibold tracking-wide uppercase">Enterprise Security</span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          Advanced <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Cybersecurity</span> Protection
        </h1>
        
        <p class="text-lg text-slate-300 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
          Safeguard your digital assets with our comprehensive security solutions. We provide 24/7 monitoring, threat detection, and rapid incident response to keep your business secure against evolving cyber threats.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
          <a routerLink="/contact" class="inline-flex justify-center items-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 shadow-lg hover:shadow-blue-500/30 transform hover:-translate-y-1">
            Get Protected
            <i class="bx bx-shield-alt-2 ml-2 text-xl"></i>
          </a>
          <a routerLink="/contact" class="inline-flex justify-center items-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-transparent border border-slate-600 rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-600">
            Security Audit
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-slate-800">
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
              <i class="bx bx-radar text-2xl"></i>
            </div>
            <div>
              <div class="text-2xl font-bold text-white">99.9%</div>
              <div class="text-xs text-slate-400 uppercase tracking-wide">Detection Rate</div>
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400">
              <i class="bx bx-time-five text-2xl"></i>
            </div>
            <div>
              <div class="text-2xl font-bold text-white">24/7</div>
              <div class="text-xs text-slate-400 uppercase tracking-wide">SOC Monitoring</div>
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400">
              <i class="bx bx-bolt-circle text-2xl"></i>
            </div>
            <div>
              <div class="text-2xl font-bold text-white">&lt;15m</div>
              <div class="text-xs text-slate-400 uppercase tracking-wide">Response Time</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="relative lg:ml-10">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-slate-700/50 bg-slate-800/50 backdrop-blur-sm">
          <img src="assets/img/cybersecurity-hero.png" alt="Cybersecurity Services" class="w-full h-auto transform hover:scale-105 transition-transform duration-700">
        </div>
        
        <!-- Floating Badge 1 -->
        <div class="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-xl animate-float-slow z-20 max-w-[180px]">
          <div class="flex items-center gap-3 mb-2">
            <div class="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <i class="bx bx-check-shield text-lg"></i>
            </div>
            <span class="text-sm font-bold text-slate-800">System Secure</span>
          </div>
          <div class="w-full bg-slate-100 rounded-full h-1.5 mb-1">
            <div class="bg-green-500 h-1.5 rounded-full" style="width: 100%"></div>
          </div>
          <div class="text-xs text-slate-500 text-right">No threats detected</div>
        </div>
        
        <!-- Floating Badge 2 -->
        <div class="absolute -bottom-6 -left-6 bg-slate-800 p-4 rounded-xl shadow-xl border border-slate-700 animate-float-delayed z-20">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <i class="bx bx-lock-alt text-xl"></i>
            </div>
            <div>
              <div class="text-sm font-bold text-white">Encrypted</div>
              <div class="text-xs text-slate-400">AES-256 Standard</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Services Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-20">
      <div class="inline-block bg-blue-500/10 border border-blue-500/40 text-blue-300 px-4 py-1 rounded-full text-sm font-semibold mb-4">
        Multi-Layered Protection
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Comprehensive <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Security Solutions</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Advanced cybersecurity services designed to protect your organization from sophisticated cyber threats with proactive monitoring, incident response, and compliance management.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Threat Detection & Response -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-700/60 transform hover:-translate-y-2 group">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
          <i class="bx bx-shield-x text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Threat Detection & Response</h3>
        <p class="text-slate-300 mb-6">
          Advanced AI-powered threat detection with rapid incident response and forensic analysis for comprehensive security coverage.
        </p>
        <ul class="space-y-3 mb-6">
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-radar text-blue-500 mr-3 text-lg"></i>
            <span>Real-time threat monitoring</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-brain text-blue-500 mr-3 text-lg"></i>
            <span>AI-powered threat detection</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-group text-blue-500 mr-3 text-lg"></i>
            <span>Incident response team</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-search-alt text-blue-500 mr-3 text-lg"></i>
            <span>Forensic analysis</span>
          </li>
        </ul>
        <div class="flex gap-6 pt-6 border-t border-slate-700/60">
          <div class="text-center">
            <div class="text-xl font-bold text-blue-400">&lt;15 min</div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">Response</div>
          </div>
          <div class="text-center">
            <div class="text-xl font-bold text-blue-400">99.9%</div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">Detection</div>
          </div>
        </div>
      </div>

      <!-- Endpoint Protection -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-700/60 transform hover:-translate-y-2 group">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
          <i class="bx bx-devices text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Endpoint Protection</h3>
        <p class="text-slate-300 mb-6">
          Comprehensive endpoint security with advanced antivirus, anti-malware, and endpoint detection and response (EDR) capabilities.
        </p>
        <ul class="space-y-3 mb-6">
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-virus text-cyan-500 mr-3 text-lg"></i>
            <span>Next-gen antivirus</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-bug-alt text-cyan-500 mr-3 text-lg"></i>
            <span>Anti-malware protection</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-desktop text-cyan-500 mr-3 text-lg"></i>
            <span>Endpoint detection & response</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-mobile text-cyan-500 mr-3 text-lg"></i>
            <span>Device control policies</span>
          </li>
        </ul>
        <div class="flex gap-6 pt-6 border-t border-slate-700/60">
          <div class="text-center">
            <div class="text-xl font-bold text-cyan-400">99.8%</div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">Success</div>
          </div>
          <div class="text-center">
            <div class="text-xl font-bold text-cyan-400">&lt;0.1%</div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">False Pos</div>
          </div>
        </div>
      </div>

      <!-- Network Security -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-700/60 transform hover:-translate-y-2 group">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
          <i class="bx bx-wifi text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Network Security</h3>
        <p class="text-slate-300 mb-6">
          Advanced network protection with next-generation firewalls, intrusion detection, and network traffic analysis.
        </p>
        <ul class="space-y-3 mb-6">
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-block text-indigo-500 mr-3 text-lg"></i>
            <span>Next-gen firewalls</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-analyse text-indigo-500 mr-3 text-lg"></i>
            <span>Intrusion detection/prevention</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-transfer text-indigo-500 mr-3 text-lg"></i>
            <span>Network traffic analysis</span>
          </li>
          <li class="flex items-center text-slate-200 text-sm">
            <i class="bx bx-shield-alt text-indigo-500 mr-3 text-lg"></i>
            <span>VPN security management</span>
          </li>
        </ul>
        <div class="flex gap-6 pt-6 border-t border-slate-700/60">
          <div class="text-center">
            <div class="text-xl font-bold text-indigo-400">10+ Gbps</div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">Speed</div>
          </div>
          <div class="text-center">
            <div class="text-xl font-bold text-indigo-400">99.99%</div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">Uptime</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Security Stack Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block cs-badge px-4 py-1 rounded-full text-sm font-semibold mb-4">
        Security Stack
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Enterprise <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Defense Layers</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Unified monitoring, detection, and protection across endpoints, network, identity, and data.
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <div class="cs-card cs-accent-1">
        <div class="cs-card-header">
          <div class="cs-card-icon"><i class="bx bx-radar"></i></div>
          <span class="cs-card-badge">SIEM</span>
        </div>
        <div class="cs-card-title">Security Information & Event Management</div>
        <p class="cs-card-desc">Centralized log collection, correlation, dashboards, and automated alerts.</p>
        <div class="cs-card-tags"><span>Log Correlation</span><span>Alerts</span><span>Dashboards</span></div>
      </div>
      <div class="cs-card cs-accent-2">
        <div class="cs-card-header">
          <div class="cs-card-icon"><i class="bx bx-shield"></i></div>
          <span class="cs-card-badge">EDR</span>
        </div>
        <div class="cs-card-title">Endpoint Detection & Response</div>
        <p class="cs-card-desc">Behavioral analytics, isolation, remediation for advanced endpoint threats.</p>
        <div class="cs-card-tags"><span>Isolation</span><span>Behavior</span><span>Remediation</span></div>
      </div>
      <div class="cs-card cs-accent-3">
        <div class="cs-card-header">
          <div class="cs-card-icon"><i class="bx bx-shield-quarter"></i></div>
          <span class="cs-card-badge">WAF</span>
        </div>
        <div class="cs-card-title">Web Application Firewall</div>
        <p class="cs-card-desc">Protect apps from OWASP Top 10, DDoS, and injection attacks.</p>
        <div class="cs-card-tags"><span>OWASP</span><span>DDoS</span><span>Bot Defense</span></div>
      </div>
      <div class="cs-card cs-accent-4">
        <div class="cs-card-header">
          <div class="cs-card-icon"><i class="bx bx-fingerprint"></i></div>
          <span class="cs-card-badge">IAM</span>
        </div>
        <div class="cs-card-title">Identity & Access Management</div>
        <p class="cs-card-desc">MFA, RBAC, just-in-time access to enforce least privilege.</p>
        <div class="cs-card-tags"><span>MFA</span><span>RBAC</span><span>JIT Access</span></div>
      </div>
      <div class="cs-card cs-accent-5">
        <div class="cs-card-header">
          <div class="cs-card-icon"><i class="bx bx-data"></i></div>
          <span class="cs-card-badge">DSPM</span>
        </div>
        <div class="cs-card-title">Data Security Posture Management</div>
        <p class="cs-card-desc">Discover sensitive data, enforce encryption, monitor exposure.</p>
        <div class="cs-card-tags"><span>Discovery</span><span>Encryption</span><span>Exposure</span></div>
      </div>
      <div class="cs-card cs-accent-6">
        <div class="cs-card-header">
          <div class="cs-card-icon"><i class="bx bx-analyse"></i></div>
          <span class="cs-card-badge">Vuln</span>
        </div>
        <div class="cs-card-title">Vulnerability Management</div>
        <p class="cs-card-desc">Continuous scanning, prioritization, and patch orchestration.</p>
        <div class="cs-card-tags"><span>CVSS</span><span>Prioritize</span><span>Patch</span></div>
      </div>
    </div>
  </div>
</section>

<!-- Compliance Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block cs-badge px-4 py-1 rounded-full text-sm font-semibold mb-4">
        Compliance & Certifications
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Verified <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Standards</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Align with industry frameworks and regulatory requirements across regions.
      </p>
    </div>
    <div class="flex flex-wrap gap-3 justify-center">
      <span class="cs-chip cs-chip-1">ISO 27001</span>
      <span class="cs-chip cs-chip-2">SOC 2</span>
      <span class="cs-chip cs-chip-3">PCI DSS</span>
      <span class="cs-chip cs-chip-4">HIPAA</span>
      <span class="cs-chip cs-chip-5">GDPR</span>
      <span class="cs-chip cs-chip-6">CERT-In</span>
    </div>
  </div>
</section>

<!-- Incident Response Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block cs-badge px-4 py-1 rounded-full text-sm font-semibold mb-4">
        Incident Response
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Proven <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Playbook</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        From detection to postmortem, a clear and rapid response procedure minimizes impact.
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <div class="cs-step-card"><div class="cs-step-num">1</div><div class="cs-step-title">Detect</div><div class="cs-step-desc">Automated alerting and triage via SIEM and EDR.</div></div>
      <div class="cs-step-card"><div class="cs-step-num">2</div><div class="cs-step-title">Contain</div><div class="cs-step-desc">Isolate affected assets, block indicators of compromise.</div></div>
      <div class="cs-step-card"><div class="cs-step-num">3</div><div class="cs-step-title">Eradicate</div><div class="cs-step-desc">Remove malware, patch vulnerabilities, rotate credentials.</div></div>
      <div class="cs-step-card"><div class="cs-step-num">4</div><div class="cs-step-title">Recover</div><div class="cs-step-desc">Restore services, validate integrity, harden configurations.</div></div>
    </div>
  </div>
</section>

<!-- Packages Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block cs-badge px-4 py-1 rounded-full text-sm font-semibold mb-4">
        Engagement Packages
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Choose Your <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Coverage</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Right-sized protection plans tailored to your compliance and risk profile.
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div class="bg-slate-900/70 rounded-2xl shadow-xl border border-slate-700/60 p-8">
        <div class="text-sm font-bold text-blue-300 mb-3">Essential</div>
        <h3 class="text-xl font-bold text-white mb-3">Baseline Security</h3>
        <ul class="space-y-2 text-slate-200 text-sm mb-6">
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>Vulnerability scanning</li>
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>WAF ruleset</li>
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>Basic SOC alerts</li>
        </ul>
        <a routerLink="/contact" class="inline-flex items-center px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors">Contact Sales</a>
      </div>
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl border border-blue-500/60 p-8 security-featured">
        <div class="text-sm font-bold text-blue-300 mb-3">Advanced</div>
        <h3 class="text-xl font-bold text-white mb-3">Managed Detection</h3>
        <ul class="space-y-2 text-slate-200 text-sm mb-6">
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>24/7 SOC monitoring</li>
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>EDR response</li>
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>Threat intel feeds</li>
        </ul>
        <a routerLink="/contact" class="inline-flex items-center px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors">Contact Sales</a>
      </div>
      <div class="bg-slate-900/70 rounded-2xl shadow-xl border border-slate-700/60 p-8">
        <div class="text-sm font-bold text-blue-300 mb-3">Enterprise</div>
        <h3 class="text-xl font-bold text-white mb-3">Zero Trust</h3>
        <ul class="space-y-2 text-slate-200 text-sm mb-6">
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>IAM hardening</li>
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>Compliance guidance</li>
          <li class="flex items-center"><i class="bx bx-check text-emerald-400 mr-2"></i>Red team exercises</li>
        </ul>
        <a routerLink="/contact" class="inline-flex items-center px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors">Contact Sales</a>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-24 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
  <div class="container mx-auto px-4">
    <div class="max-w-4xl mx-auto text-center">
      <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
        Secure Your Digital Assets <span class="text-blue-200">Today</span>
      </h2>
      <p class="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
        Don't wait for a cyber attack to happen. Protect your business with our comprehensive cybersecurity solutions and expert security team. Get started with a free security assessment.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-6 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-blue-700 font-bold py-4 px-10 rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl flex items-center justify-center group">
          <span>Contact Us for Details</span>
          <i class="bx bx-right-arrow-alt ml-2 text-xl group-hover:translate-x-1 transition-transform"></i>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold py-4 px-10 rounded-xl transition-all duration-300 flex items-center justify-center group">
          <span>Talk to Security Expert</span>
          <i class="bx bx-headphone ml-2 text-xl"></i>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">24/7</div>
          <div class="text-sm text-blue-100">SOC Monitoring</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">99.9%</div>
          <div class="text-sm text-blue-100">Threat Detection</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">&lt;15min</div>
          <div class="text-sm text-blue-100">Response Time</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">100%</div>
          <div class="text-sm text-blue-100">Compliance</div>
        </div>
      </div>
    </div>
  </div>
</section>
